import * as React from 'react';
import { CollapsibleCard } from '@insolech/ui';
import {
  PatientPickerBody,
  type PatientPickerValue,
  type ReorderPrefill,
} from '../PatientPicker';
import type { SolutionSubtype } from '../SolutionSubtypePicker';

/**
 * PatientSection - R32 - embeds the patient picker as the first section
 * of the prescription form, matching the CollapsibleCard density of the
 * Rearfoot / Midfoot / Forefoot / Offloadings sections.
 *
 * The patient state still lives at the wizard level (so save-as-draft
 * gating, submission, and the unsaved-changes guard keep working) - this
 * component just renders the picker inline so the operator can capture
 * patient + prescription on a single step.
 */
interface Props {
  value: PatientPickerValue;
  onChange: (next: PatientPickerValue) => void;
  showErrors?: boolean | undefined;
  defaultOpen?: boolean | undefined;
  open?: boolean | undefined;
  onOpenChange?: ((next: boolean) => void) | undefined;
  /** R35 — multi-clinic users pick the target clinic at the wizard level;
   *  forwarded so dupe detection + existing-patient search scope correctly. */
  clinicId?: string | null;
  /** The order's own clinic, never widened to null for admins — forwarded so a
   *  record from another clinic cannot be adopted onto this order. */
  orderClinicId?: string | null;
  /** Product family chosen in the Subtype step — forwarded so a reorder
   *  prefill only copies the prescription cards shared with it. */
  subtype?: SolutionSubtype | null | undefined;
  /** Forwarded so the prior-order modal's reset confirms before discarding a
   *  prescription the operator already filled in. */
  prescriptionHasContent?: boolean | undefined;
  /** Forwarded so picking a past order can prefill patient + prescription
   *  atomically at the wizard level. */
  onReorderPrefill?: ((next: ReorderPrefill) => void) | undefined;
  /** The ONE sandal flag PrescriptionForm computes (subtype OR line_type
   *  spelling), forwarded so the patient form hides the shoe-related fields
   *  by the same rule the payload builders and the wizard's gate use. */
  isSandal?: boolean | undefined;
}

export function PatientSection({
  value,
  onChange,
  showErrors,
  defaultOpen = true,
  open,
  onOpenChange,
  clinicId,
  orderClinicId,
  subtype,
  prescriptionHasContent,
  onReorderPrefill,
  isSandal = false,
}: Props) {
  const fullName = (value.formState.full_name ?? '').trim();
  const subtitle = value.reorderFromOrder
    ? `Reorder · ${value.display_name ?? fullName ?? 'selected'} · from ${value.reorderFromOrder.orderNumber}`
    : value.patientId
      ? `Existing patient · ${value.display_name ?? fullName ?? 'selected'}${
          value.patient_code ? ` · ${value.patient_code}` : ''
        }`
      : fullName
        ? `New patient · ${fullName}`
        : 'Pick an existing patient or fill in a new one to enable Save draft.';

  const isControlled = open !== undefined && onOpenChange !== undefined;

  return (
    <CollapsibleCard
      title="Patient"
      subtitle={subtitle}
      {...(isControlled
        ? { open: open!, onOpenChange: onOpenChange! }
        : { defaultOpen })}
    >
      <PatientPickerBody
        value={value}
        onChange={onChange}
        showErrors={showErrors}
        {...(clinicId !== undefined ? { clinicId } : {})}
        {...(orderClinicId !== undefined ? { orderClinicId } : {})}
        subtype={subtype ?? null}
        prescriptionHasContent={prescriptionHasContent ?? false}
        {...(onReorderPrefill ? { onReorderPrefill } : {})}
        isSandal={isSandal}
      />
    </CollapsibleCard>
  );
}
